import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Stethoscope, Pill, Activity, Receipt, Calendar, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PatientHistory = () => {
  const { patientId } = useParams();
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/patients/${patientId}/history`);
      setHistory(data);
    } catch (e) {
      console.error('Error fetching history:', e);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="text-center py-12" style={{ color: '#6B7280' }}>Loading patient history...</div>;
  }

  if (!history) {
    return <div className="text-center py-12" style={{ color: '#6B7280' }}>Patient not found</div>;
  }

  // Build unified timeline
  const timeline = [];
  history.consultations.forEach(c => timeline.push({ type: 'consultation', date: c.date, data: c }));
  history.procedures.forEach(p => timeline.push({ type: 'procedure', date: p.startDate, data: p }));
  history.bills.forEach(b => timeline.push({ type: 'bill', date: b.date, data: b }));
  timeline.sort((a, b) => new Date(b.date) - new Date(a.date));

  const getIconBg = (type) => {
    switch (type) {
      case 'consultation': return { bg: '#F5E6F5', color: '#990099', Icon: Stethoscope };
      case 'procedure': return { bg: '#FEF3C7', color: '#D97706', Icon: Activity };
      case 'bill': return { bg: '#DBEAFE', color: '#1E40AF', Icon: Receipt };
      default: return { bg: '#F5E6F5', color: '#990099', Icon: Calendar };
    }
  };

  return (
    <div>
      <Link to="/patients" className="inline-flex items-center gap-2 mb-6 text-sm font-medium" style={{ color: '#990099' }} data-testid="back-to-patients">
        <ArrowLeft size={18} />
        Back to Patients
      </Link>

      <div className="card p-6 mb-8" data-testid="patient-info-card">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: '#F5E6F5' }}>
            <User size={28} style={{ color: '#990099' }} />
          </div>
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
              {history.patient.name || history.patient.phone}
            </h1>
            <div className="flex gap-6 mt-2 text-sm" style={{ color: '#6B7280' }}>
              {history.patient.age && <span>{history.patient.age} years</span>}
              {history.patient.gender && <span>{history.patient.gender}</span>}
              <span>{history.patient.phone}</span>
            </div>
            {history.patient.address && <p className="text-sm mt-1" style={{ color: '#6B7280' }}>{history.patient.address}</p>}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t" style={{ borderColor: '#E5E0D8' }}>
          <div className="text-center">
            <p className="text-2xl font-bold" style={{ color: '#990099' }}>{history.consultations.length}</p>
            <p className="text-xs uppercase tracking-wide mt-1" style={{ color: '#6B7280' }}>Consultations</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold" style={{ color: '#D97706' }}>{history.procedures.length}</p>
            <p className="text-xs uppercase tracking-wide mt-1" style={{ color: '#6B7280' }}>Procedures</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold" style={{ color: '#1E40AF' }}>{history.bills.length}</p>
            <p className="text-xs uppercase tracking-wide mt-1" style={{ color: '#6B7280' }}>Bills</p>
          </div>
        </div>
      </div>

      <h2 className="text-2xl font-bold mb-6" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
        Medical Timeline
      </h2>

      {timeline.length === 0 ? (
        <div className="card p-12 text-center" data-testid="empty-timeline">
          <Calendar size={48} style={{ color: '#6B7280', margin: '0 auto' }} />
          <p className="mt-4" style={{ color: '#6B7280' }}>No medical history yet</p>
        </div>
      ) : (
        <div className="relative" data-testid="timeline">
          {/* Vertical line */}
          <div className="absolute left-7 top-0 bottom-0 w-0.5" style={{ backgroundColor: '#E5E0D8' }} />

          {timeline.map((entry, idx) => {
            const { bg, color, Icon } = getIconBg(entry.type);
            return (
              <div key={idx} className="relative pl-20 pb-8" data-testid={`timeline-${entry.type}-${idx}`}>
                <div
                  className="absolute left-2 w-11 h-11 rounded-full flex items-center justify-center border-4"
                  style={{ backgroundColor: bg, borderColor: '#F9F6F0' }}
                >
                  <Icon size={20} style={{ color }} />
                </div>

                <div className="card p-5">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <span
                        className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium uppercase tracking-wide"
                        style={{ backgroundColor: bg, color }}
                      >
                        {entry.type}
                      </span>
                      <p className="text-sm mt-2" style={{ color: '#6B7280' }}>{formatDate(entry.date)}</p>
                    </div>
                  </div>

                  {entry.type === 'consultation' && (
                    <div>
                      <p className="font-medium mb-2" style={{ color: '#2D3A2A' }}>
                        Dr. {entry.data.doctorName}
                      </p>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-xs uppercase tracking-wide" style={{ color: '#6B7280' }}>Symptoms: </span>
                          <span style={{ color: '#2D3A2A' }}>{entry.data.symptoms}</span>
                        </div>
                        <div>
                          <span className="text-xs uppercase tracking-wide" style={{ color: '#6B7280' }}>Diagnosis: </span>
                          <span style={{ color: '#2D3A2A' }}>{entry.data.diagnosis}</span>
                        </div>
                        {entry.data.notes && (
                          <div>
                            <span className="text-xs uppercase tracking-wide" style={{ color: '#6B7280' }}>Notes: </span>
                            <span style={{ color: '#2D3A2A' }}>{entry.data.notes}</span>
                          </div>
                        )}
                        {entry.data.prescription && entry.data.prescription.medicines && (
                          <div className="mt-3 pt-3 border-t" style={{ borderColor: '#E5E0D8' }}>
                            <p className="text-xs uppercase tracking-wide mb-2" style={{ color: '#6B7280' }}>
                              <Pill size={12} className="inline mr-1" />Prescription
                            </p>
                            <div className="space-y-1">
                              {entry.data.prescription.medicines.map((m, i) => (
                                <div key={i} className="text-sm pl-3" style={{ color: '#2D3A2A' }}>
                                  • <strong>{m.medicineName}</strong> — {m.dosage}, {m.duration} ({m.instructions})
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {entry.type === 'procedure' && (() => {
                    const statusColors = {
                      COMPLETED: { bg: '#D1FAE5', text: '#065F46' },
                      IN_PROGRESS: { bg: '#DBEAFE', text: '#1E40AF' },
                      PLANNED: { bg: '#FEE2E2', text: '#991B1B' },
                    };
                    const sc = statusColors[entry.data.status] || statusColors.PLANNED;
                    return (
                    <div>
                      <p className="font-medium mb-2" style={{ color: '#2D3A2A' }}>{entry.data.procedureName}</p>
                      <div className="flex flex-wrap gap-4 text-sm">
                        <span style={{ color: '#6B7280' }}>
                          Sessions: <strong style={{ color: '#2D3A2A' }}>{entry.data.completedSessions}/{entry.data.sessionCount}</strong>
                        </span>
                        <span
                          className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{ backgroundColor: sc.bg, color: sc.text }}
                        >
                          {entry.data.status}
                        </span>
                      </div>
                      {(entry.data.doctorName || entry.data.therapistName) && (
                        <div className="mt-2 text-sm flex flex-wrap gap-4">
                          {entry.data.doctorName && (
                            <span style={{ color: '#6B7280' }}>Doctor: <strong style={{ color: '#2D3A2A' }}>{entry.data.doctorName}</strong></span>
                          )}
                          {entry.data.therapistName && (
                            <span style={{ color: '#6B7280' }}>Therapist: <strong style={{ color: '#2D3A2A' }}>{entry.data.therapistName}</strong></span>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })()}

                  {entry.type === 'bill' && (
                    <div>
                      <p className="font-medium mb-2" style={{ color: '#2D3A2A' }}>
                        {entry.data.billNumber}
                      </p>
                      <div className="flex gap-4 text-sm">
                        <span style={{ color: '#6B7280' }}>
                          Items: <strong style={{ color: '#2D3A2A' }}>{entry.data.items.length}</strong>
                        </span>
                        <span style={{ color: '#6B7280' }}>
                          Mode: <strong style={{ color: '#2D3A2A' }}>{entry.data.paymentMode}</strong>
                        </span>
                        <span style={{ color: '#6B7280' }}>
                          Total: <strong style={{ color: '#990099' }}>₹{entry.data.totalAmount.toFixed(2)}</strong>
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PatientHistory;
